import React, { useState, useEffect } from 'react';
import { LaborSafetyInstructionItem, LaborSafetyInstructionItemFormData, Employee, LaborSafetyInstructionType } from '../../types/models';
import { LaborSafetyInstructionItemService } from '../../services/laborSafetyInstructionItemService';
import { EmployeeService } from '../../services/employeeService';
import { EmployeeAutocomplete } from '../EmployeeAutocomplete';
import { formatDateForInput } from '../../utils/dateUtils';

interface LaborSafetyInstructionItemFormProps {
  instruction?: LaborSafetyInstructionItem;
  employeeId?: number;
  onSuccess: (createdItem?: LaborSafetyInstructionItem) => void;
  onCancel: () => void;
}

export const LaborSafetyInstructionItemForm: React.FC<LaborSafetyInstructionItemFormProps> = ({
  instruction,
  employeeId,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<LaborSafetyInstructionItemFormData>({
    employeeId: instruction?.employeeId || employeeId || 0,
    responsibleEmployeeId: instruction?.responsibleEmployeeId || 0,
    date: instruction?.date ? new Date(instruction.date).toISOString().split('T')[0] : '',
    type: instruction?.type || LaborSafetyInstructionType.First,
    reason: instruction?.reason || '',
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Загружаем всех сотрудников
      const employeesResponse = await EmployeeService.getAll();

      if (employeesResponse.success) {
        setEmployees(employeesResponse.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      if (instruction) {
        // Обновление
        response = await LaborSafetyInstructionItemService.update(instruction.id, { ...formData, id: instruction.id });
      } else {
        // Создание
        response = await LaborSafetyInstructionItemService.create(formData);
      }

      if (response.success) {
        // Для создания передаем созданный элемент, для обновления - ничего
        onSuccess(instruction ? undefined : (response.data as LaborSafetyInstructionItem));
      } else {
        setError(response.message || 'Ошибка сохранения');
      }
    } catch (err) {
      setError('Произошла ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Id') ? Number(value) : value
    }));
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{ maxWidth: '800px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card" style={{ margin: 0, boxShadow: 'none' }}>
          <h3>{instruction ? 'Редактирование инструктажа по охране труда' : 'Создание инструктажа по охране труда'}</h3>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Сотрудник *</label>
            <EmployeeAutocomplete
              value={formData.employeeId}
              onChange={(employeeId) => setFormData(prev => ({ ...prev, employeeId: employeeId || 0 }))}
              placeholder="Начните вводить ФИО сотрудника..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Ответственный сотрудник *</label>
            <EmployeeAutocomplete
              value={formData.responsibleEmployeeId}
              onChange={(employeeId) => setFormData(prev => ({ ...prev, responsibleEmployeeId: employeeId || 0 }))}
              placeholder="Начните вводить ФИО ответственного..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Дата проведения *</label>
            <input
              type="date"
              name="date"
              className="form-input"
              value={formatDateForInput(formData.date)}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Вид проводимого инструктажа *</label>
            <select
              name="type"
              className="form-input"
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: Number(e.target.value) as LaborSafetyInstructionType }))}
              required
            >
              <option value={LaborSafetyInstructionType.First}>Первичный</option>
              <option value={LaborSafetyInstructionType.Repeat}>Повторный</option>
              <option value={LaborSafetyInstructionType.Unscheduled}>Внеплановый</option>
              <option value={LaborSafetyInstructionType.Targeted}>Целевой</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Причина проведения инструктажа</label>
            <textarea
              name="reason"
              className="form-input"
              value={formData.reason || ''}
              onChange={handleChange}
              rows={3}
              placeholder="Укажите причину проведения инструктажа..."
            />
          </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Сохранение...' : (instruction ? 'Сохранить' : 'Создать')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};